"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef } from "react";

import type { StoryChapter } from "@/lib/stories";
import { cn } from "@/lib/utils";

import { PORTRAIT_SLIDE, useStoryStage, VIDEO_SLIDE } from "./story-stage";
import StoryVideo from "./story-video";
import { useIdleChrome } from "./use-idle-chrome";
import { useVideoTransport } from "./use-video-transport";
import VideoTransportControls from "./video-transport";
import { youtubeEmbedSrc } from "./youtube-embed";

export interface StoryMediaProps {
  name: string;
  role: string;
  location: string;
  image: string;
  imageAlt: string;
  youtubeUrl?: string;
  videoPoster?: string;
  videoDuration?: string;
  chapters: StoryChapter[];
  /**
   * Whether to show the portrait panel beside the clip. False when the story's
   * image is the clip's own poster frame, where a "photo" panel would be a still
   * of the video sitting next to the video.
   */
  showPortrait?: boolean;
}

function Chevron({ back = false }: { back?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden fill="none">
      <path
        d={back ? "M9.5 3.5 5 8l4.5 4.5" : "M6.5 3.5 11 8l-4.5 4.5"}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Speaker card. On the portrait it sits at the lower left, clear of the
 * subject's face and matching the name plate on an index deck card; on the clip
 * it stays at the top left, where it does not fight the transport bar and where
 * the index player puts it, so the two players still read as one component.
 *
 * Insets grow at lg: at 840px wide the old flat 32px left the name looking stuck
 * to the corner.
 */
function Speaker({
  name,
  role,
  location,
  placement = "top",
  className,
}: {
  name: string;
  role: string;
  location: string;
  placement?: "top" | "bottom";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute left-6 sm:left-8 lg:left-10",
        placement === "top"
          ? "top-5 sm:top-6 lg:top-8"
          : "bottom-5 sm:bottom-6 lg:bottom-8",
        className,
      )}
    >
      <p className="text-[1.25rem] font-medium tracking-tight text-white sm:text-[1.375rem]">
        {name}
      </p>
      <p className="mt-1.5 font-mono text-[0.625rem] tracking-[0.1em] text-white/60 uppercase">
        {role} · {location}
      </p>
    </div>
  );
}

/**
 * The story's media: the clip first where there is one, the expert's portrait
 * after it, on a scroll-snap track so touch keeps native momentum. A story with
 * only a photo shows just the photo, and one whose photo is the clip's own poster
 * shows just the clip. The arrows and the chapter rail drive the same position
 * through `StoryStage`, and the clip wears the same overlay and controls as the
 * player on the index.
 */
export default function StoryMedia({
  name,
  role,
  location,
  image,
  imageAlt,
  youtubeUrl,
  videoPoster,
  videoDuration,
  chapters,
  showPortrait = true,
}: StoryMediaProps) {
  const {
    slide,
    setSlide,
    registerVideo,
    registerFrame,
    activeChapter,
    setActiveChapter,
  } = useStoryStage();

  const transport = useVideoTransport();
  const { playing, elapsed } = transport;

  const trackRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  const chrome = useIdleChrome(frameRef, playing);

  useEffect(() => {
    registerVideo(transport.video);
    registerFrame(frameRef.current);
    return () => {
      registerVideo(null);
      registerFrame(null);
    };
  }, [registerFrame, registerVideo, transport.video]);

  // Slides are exactly one track width apart, so a target more than half a
  // width away can only have come from an arrow or the rail. Anything closer is
  // the user's own drag, which snap handles better than a second animation.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) {
      return;
    }
    const left = slide * track.clientWidth;
    if (Math.abs(track.scrollLeft - left) < track.clientWidth * 0.5) {
      return;
    }
    track.scrollTo({ left, behavior: "smooth" });
  }, [slide]);

  const onScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) {
      return;
    }
    const next = Math.round(track.scrollLeft / track.clientWidth);
    if (next !== slide) {
      setSlide(next);
    }
  }, [setSlide, slide]);

  useEffect(() => {
    if (chapters.length === 0) {
      return;
    }
    let next = 0;
    chapters.forEach((chapter, index) => {
      if (elapsed >= chapter.time) {
        next = index;
      }
    });
    if (next !== activeChapter) {
      setActiveChapter(next);
    }
  }, [activeChapter, chapters, elapsed, setActiveChapter]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void frameRef.current?.requestFullscreen?.();
    }
  }, []);

  const clip = youtubeEmbedSrc(youtubeUrl);
  const posterSrc = videoPoster ?? image;

  // Applied element by element rather than through one wrapper: a full-frame
  // overlay would swallow the click that pauses the clip.
  const chromeClass = cn(
    "transition-opacity duration-500",
    chrome.visible ? "opacity-100" : "opacity-0",
  );

  return (
    <div
      ref={frameRef}
      className="relative overflow-hidden rounded-2xl bg-neutral-950 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.35)]"
    >
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {/* --- Clip, first when there is one --- */}
        {clip && (
          <div className="relative aspect-video w-full shrink-0 snap-center">
            <StoryVideo
              src={clip}
              poster={posterSrc}
              posterSizes="(max-width: 1024px) 100vw, 880px"
              posterPriority
              title={`${name} on Terac`}
              started={transport.started}
              attach={transport.attach}
              onPress={() => {
                // A tap that brought the chrome back is not also asking to
                // pause. On a pointer device the chrome is up long before the
                // click, so this only changes what the first touch does.
                if (!chrome.pressRevealed()) {
                  transport.togglePlay();
                }
              }}
            />

            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent",
                chromeClass,
              )}
            />
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/75 to-transparent",
                chromeClass,
              )}
            />

            <Speaker
              name={name}
              role={role}
              location={location}
              className={chromeClass}
            />

            {!playing && (
              <button
                type="button"
                onClick={transport.togglePlay}
                aria-label={`Play ${name}'s story`}
                // z-10 because the controls are painted after this and their
                // container reaches a few pixels past the middle of a
                // phone-sized frame: without it the disc is there but dead.
                className="absolute top-1/2 left-1/2 z-10 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] transition-transform hover:scale-105"
              >
                <svg
                  viewBox="0 0 16 18"
                  className="ml-1 size-5 text-neutral-950"
                  aria-hidden
                  fill="currentColor"
                >
                  <path d="M2 1.5 L14.5 9 L2 16.5 Z" />
                </svg>
              </button>
            )}

            <VideoTransportControls
              transport={transport}
              chapters={chapters}
              runtime={videoDuration}
              onToggleFullscreen={toggleFullscreen}
              retracted={!chrome.visible}
              className={chromeClass}
            />
          </div>
        )}

        {/* --- Portrait, after the clip --- */}
        {showPortrait && (
          <div className="relative aspect-video w-full shrink-0 snap-center">
            <Image
              src={image}
              alt={imageAlt}
              fill
              sizes="(max-width: 1024px) 100vw, 880px"
              className="object-cover"
              priority
            />
            {/* Scrim under the name rather than over the top of the frame: the
                name sits low, so the gradient follows it. Same height and
                strength as an index deck card, since this is now the same
                composition. */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/60 to-transparent"
            />
            <Speaker
              name={name}
              role={role}
              location={location}
              placement="bottom"
            />
          </div>
        )}
      </div>

      {/* --- Swipe affordances: only with two panels to move between --- */}
      {clip && showPortrait && (
        <>
          <button
            type="button"
            onClick={() => {
              setSlide(PORTRAIT_SLIDE);
            }}
            aria-label="Swipe to the photo"
            className={cn(
              "absolute top-1/2 right-4 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-sm transition-[opacity,background-color] duration-300 hover:bg-black/70 sm:right-6",
              slide === VIDEO_SLIDE && !playing
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
          >
            <Chevron />
          </button>

          <button
            type="button"
            onClick={() => {
              setSlide(VIDEO_SLIDE);
            }}
            aria-label="Back to the video"
            // Pointer-only: on a phone the frame is ~190px tall and this lands
            // on the chapter label, where a swipe back does the same job.
            className={cn(
              "absolute top-1/2 left-4 hidden size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-sm transition-[opacity,background-color] duration-300 hover:bg-black/70 sm:left-6 sm:flex",
              slide === PORTRAIT_SLIDE
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
          >
            <Chevron back />
          </button>
        </>
      )}

      {/* Runtime and, with two panels, which one is showing. Both step aside
          once the clip is running. */}
      {clip && (
        <div
          className={cn(
            "absolute top-5 right-6 flex items-center gap-3 transition-opacity duration-300 sm:top-6 sm:right-8 lg:top-8 lg:right-10",
            playing ? "pointer-events-none opacity-0" : "opacity-100",
          )}
        >
          {videoDuration && (
            <span className="rounded-full bg-black/45 px-2.5 py-1 font-mono text-[0.6875rem] tracking-[0.04em] text-white backdrop-blur-sm">
              {videoDuration}
            </span>
          )}
          {showPortrait && (
            <div aria-hidden className="flex items-center gap-1.5">
              {[VIDEO_SLIDE, PORTRAIT_SLIDE].map((dot) => (
                <span
                  key={dot}
                  className={cn(
                    "h-1.5 rounded-full transition-[width,background-color] duration-300",
                    slide === dot ? "w-5 bg-white" : "w-1.5 bg-white/45",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
